/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: rodeo_round_configs entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the rodeo_round_configs table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the rodeo_round_configs API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, rodeo_id, event_id, round_number, round_name, performance_id, is_go_round, is_short_round, advancing_count, sort_order, metadata, created_by, created_at, updated_by, updated_at, run_order_mode]
 * Searchable fields: []
 * Filterable fields: [id, rodeo_id, event_id, performance_id, run_order_mode]
 */

import { httpClient } from '../http-client.js';
import type { UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  RodeoRoundConfigsRow,
  RodeoRoundConfigsInsert,
  RodeoRoundConfigsUpdate
} from '../../types/generated_schemas/generated-rodeo_round_configs-schema.js';

export interface RodeoRoundConfigsQueryOptions extends UnifiedQueryOptions {}

export interface RodeoRoundConfigsResponse {
  data: RodeoRoundConfigsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for rodeo_round_configs table operations
 *
 * @description Provides type-safe access to rodeo_round_configs API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class RodeoRoundConfigsClient {
  private readonly basePath = '/rodeo-round-configs';

  /**
   * Get all rodeo_round_configs records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of rodeo_round_configs records
   */
  async getAll(options?: RodeoRoundConfigsQueryOptions): Promise<RodeoRoundConfigsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<RodeoRoundConfigsResponse>(url);
    return response.data;
  }

  /**
   * Get a single rodeo_round_configs record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the rodeo_round_configs record
   */
  async getById(id: string): Promise<RodeoRoundConfigsRow> {
    const response = await httpClient.get<{
      data: RodeoRoundConfigsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new rodeo_round_configs record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created rodeo_round_configs record
   */
  async create(data: RodeoRoundConfigsInsert): Promise<RodeoRoundConfigsRow> {
    const response = await httpClient.post<{
      data: RodeoRoundConfigsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing rodeo_round_configs record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated rodeo_round_configs record
   */
  async update(id: string, data: RodeoRoundConfigsUpdate): Promise<RodeoRoundConfigsRow> {
    const response = await httpClient.put<{
      data: RodeoRoundConfigsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a rodeo_round_configs record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple rodeo_round_configs records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created rodeo_round_configs records
   */
  async createMany(data: RodeoRoundConfigsInsert[]): Promise<RodeoRoundConfigsRow[]> {
    const response = await httpClient.post<{
      data: RodeoRoundConfigsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple rodeo_round_configs records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search rodeo_round_configs with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: RodeoRoundConfigsQueryOptions
  ): Promise<RodeoRoundConfigsResponse> {
    throw new Error('Search operations are not available for rodeo_round_configs - no searchable fields configured');
  }

  /**
   * Get count of rodeo_round_configs records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a rodeo_round_configs record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'rodeo_round_configs',
      publicFields: ['id', 'rodeo_id', 'event_id', 'round_number', 'round_name', 'performance_id', 'is_go_round', 'is_short_round', 'advancing_count', 'sort_order', 'metadata', 'created_by', 'created_at', 'updated_by', 'updated_at', 'run_order_mode'],
      searchableFields: [],
      filterableFields: ['id', 'rodeo_id', 'event_id', 'performance_id', 'run_order_mode'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'rodeo_id', 'event_id', 'round_number', 'is_go_round', 'is_short_round', 'sort_order', 'created_at', 'updated_at', 'run_order_mode'],
    };
  }
}

// Export singleton instance for convenience
export const rodeoRoundConfigsClient = new RodeoRoundConfigsClient();
